/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

interface PageMenuActionsProps {
  /** True below the desktop breakpoint, where the menu offers no actions. */
  isMobile: boolean
  /** Schedule pages fade the tab strip out under the actions. */
  isSchedule?: boolean
  /** The page's header actions. */
  children: ReactNode
}

/**
 * The header actions beside an organization, team or schedule's tabs.
 *
 * They mount on the first desktop-width render and then stay mounted: below
 * the breakpoint they are only hidden. A dialog an action opened, such as a
 * roster import with a request in flight or a duplicate review and its
 * choices, therefore survives the window narrowing, guards included. A page
 * first rendered at mobile width still mounts none of them, so narrow screens
 * gain no actions.
 */
const PageMenuActions = ({
  isMobile,
  isSchedule = false,
  children,
}: PageMenuActionsProps) => {
  'use memo'
  const [hasShownOnDesktop, setHasShownOnDesktop] = useState(!isMobile)
  const [hideShadow, setHideShadow] = useState(false)
  const actionsRef = useRef<HTMLDivElement>(null)

  if (!isMobile && !hasShownOnDesktop) {
    setHasShownOnDesktop(true)
  }

  useLayoutEffect(() => {
    // A hidden row always measures 0 wide: measure it once it shows again.
    if (isMobile) {
      return
    }
    if (actionsRef.current?.getBoundingClientRect().width === 0) {
      setHideShadow(true)
    }

    return () => {
      setHideShadow(false)
    }
  }, [children, isMobile])

  if (!hasShownOnDesktop) {
    return null
  }

  return (
    <div
      ref={actionsRef}
      css={[
        actionsStyles,
        isSchedule && scheduleShadowStyles(hideShadow),
        isMobile && hiddenStyles,
      ]}
    >
      {children}
    </div>
  )
}

export default PageMenuActions

// ---- styles ----

const actionsStyles = css`
  position: relative;
  display: flex;
  margin-left: auto;

  .header-action {
    margin-left: 8px;
  }
`

// Fades the tabs out under the actions, unless the actions render nothing.
const scheduleShadowStyles = (hideShadow: boolean) => css`
  &::before {
    position: absolute;
    top: -8px;
    left: -42px;
    bottom: -7px;
    content: '';
    width: ${hideShadow ? 0 : 42}px;
    background: linear-gradient(
      to left,
      ${colors.HEADER_BG_2},
      ${colors.HEADER_BG_2} 20%,
      transparent
    );
    pointer-events: none;
  }
`

const hiddenStyles = css`
  display: none;
`
